import { Command } from "commander"
import pc from "picocolors"
import {
  type Address,
  createPublicClient,
  http,
  isAddress,
  zeroAddress,
} from "viem"
import { validateManifest } from "../../lib/manifest/index.js"
import {
  CompositePredicateABI,
  ERC721OwnerPredicateABI,
  ERC1155OwnerPredicateABI,
  IAccessPredicateABI,
  SubscriptionPredicateABI,
} from "../../lib/onchain/abis.js"
import { decodeRequirement } from "../../lib/onchain/access.js"
import { computeManifestHash } from "../../lib/onchain/hash.js"
import { ToolRegistryClient } from "../../lib/onchain/registry.js"
import { getChain, NETWORK_OPTION_DESCRIPTION } from "./get-chain.js"
import {
  classifyResolvedHostname,
  isPrivateHostname,
  printProbeResult,
  probeEndpoint,
} from "./probe-endpoint.js"
import {
  parseToolId,
  RPC_URL_OPTION_DESCRIPTION,
  resolveRpcUrl,
} from "./shared.js"

interface InspectOptions {
  toolId: string
  network: string
  checkAccess?: string
  rpcUrl?: string
}

export const inspectCommand = new Command("inspect")
  .description(
    "Read onchain tool state and cross-check against the live manifest",
  )
  .option("--tool-id <id>", "Numeric tool ID (required)")
  .option("--network <network>", NETWORK_OPTION_DESCRIPTION, "base")
  .option(
    "--check-access <address>",
    "Check whether an address has access to the tool",
  )
  .option("--rpc-url <url>", RPC_URL_OPTION_DESCRIPTION)
  .action(async (options: InspectOptions) => {
    if (!options.toolId) {
      console.error(pc.red("Error: --tool-id is required"))
      process.exit(1)
    }

    const toolId = parseToolId(
      options.toolId,
      "Error: --tool-id must be a numeric value",
    )

    const chain = getChain(options.network)
    const rpcUrl = resolveRpcUrl(options.rpcUrl, undefined, chain)
    const client = new ToolRegistryClient({ chain, rpcUrl })

    console.log(pc.cyan("Reading onchain tool config...\n"))

    let config: Awaited<ReturnType<ToolRegistryClient["getToolConfig"]>>
    try {
      config = await client.getToolConfig(toolId)
    } catch (err) {
      console.error(pc.red("Error: Failed to read tool config"))
      console.error(err instanceof Error ? err.message : String(err))
      process.exit(1)
    }

    console.log(pc.cyan("Onchain data:"))
    console.log(`  Creator:          ${config.creator}`)
    console.log(`  Metadata URI:     ${config.metadataURI}`)
    console.log(`  Manifest Hash:    ${config.manifestHash}`)
    console.log(`  Access Predicate: ${config.accessPredicate}`)

    if (config.accessPredicate !== zeroAddress) {
      const publicClient = createPublicClient({
        chain,
        transport: http(rpcUrl),
      })

      let predicateName: string | undefined
      try {
        predicateName = await publicClient.readContract({
          address: config.accessPredicate,
          abi: IAccessPredicateABI,
          functionName: "name",
        })
      } catch {
        // predicate may not implement name()
      }

      console.log(`  Predicate name:   ${predicateName ?? "<unknown>"}`)

      if (predicateName === "ERC721OwnerPredicate") {
        try {
          const collections = await publicClient.readContract({
            address: config.accessPredicate,
            abi: ERC721OwnerPredicateABI,
            functionName: "getCollections",
            args: [toolId],
          })
          console.log("  Collections:")
          for (let i = 0; i < collections.length; i++) {
            console.log(`    [${i}] ${collections[i]}`)
          }
        } catch (err) {
          console.error(
            pc.yellow(
              `  Warning: Failed to read collections: ${err instanceof Error ? err.message : String(err)}`,
            ),
          )
        }
      } else if (predicateName === "ERC1155OwnerPredicate") {
        try {
          const entries = await publicClient.readContract({
            address: config.accessPredicate,
            abi: ERC1155OwnerPredicateABI,
            functionName: "getCollectionTokens",
            args: [toolId],
          })
          console.log("  Collection tokens:")
          for (let i = 0; i < entries.length; i++) {
            console.log(`    [${i}] ${entries[i].collection}`)
            console.log(
              `        Token IDs: ${entries[i].tokenIds.map(String).join(", ")}`,
            )
          }
        } catch (err) {
          console.error(
            pc.yellow(
              `  Warning: Failed to read collection tokens: ${err instanceof Error ? err.message : String(err)}`,
            ),
          )
        }
      } else if (predicateName === "SubscriptionPredicate") {
        try {
          const gatingConfig = await publicClient.readContract({
            address: config.accessPredicate,
            abi: SubscriptionPredicateABI,
            functionName: "getToolGatingConfig",
            args: [toolId],
          })
          console.log(`  Subscription collection: ${gatingConfig.collection}`)
          console.log(
            `  Min tier: ${gatingConfig.minTier === 0 ? "any active subscription" : gatingConfig.minTier}`,
          )
        } catch (err) {
          console.error(
            pc.yellow(
              `  Warning: Failed to read subscription config: ${err instanceof Error ? err.message : String(err)}`,
            ),
          )
        }
      } else if (predicateName === "CompositePredicate") {
        try {
          const [op, terms] = await Promise.all([
            publicClient.readContract({
              address: config.accessPredicate,
              abi: CompositePredicateABI,
              functionName: "getOp",
              args: [toolId],
            }),
            publicClient.readContract({
              address: config.accessPredicate,
              abi: CompositePredicateABI,
              functionName: "getTerms",
              args: [toolId],
            }),
          ])
          console.log(`  Composite op: ${op === 0 ? "ALL (AND)" : "ANY (OR)"}`)
          console.log(`  Terms (${terms.length}):`)
          for (let i = 0; i < terms.length; i++) {
            const negStr = terms[i].negate ? " (negated)" : ""
            console.log(`    [${i}] ${terms[i].predicate}${negStr}`)
          }
        } catch (err) {
          console.error(
            pc.yellow(
              `  Warning: Failed to read composite config: ${err instanceof Error ? err.message : String(err)}`,
            ),
          )
        }
      } else {
        // Fall through to generic getRequirements()-based introspection.
        // Dispatch on each requirement's kind, not the predicate's name —
        // third-party predicates (e.g. attestation issuers) pick their own
        // name() but the kind selector is the stable identifier for the
        // requirement shape.
        try {
          const [requirements, logic] = await publicClient.readContract({
            address: config.accessPredicate,
            abi: IAccessPredicateABI,
            functionName: "getRequirements",
            args: [toolId],
          })
          if (requirements.length > 0) {
            console.log(
              pc.cyan(
                `\n  Access requirements (${logic === 0 ? "AND" : "OR"}, advisory):`,
              ),
            )
            for (let i = 0; i < requirements.length; i++) {
              const r = requirements[i]
              const decoded = decodeRequirement(r)
              const label = r.label || "<no label>"
              if (decoded.type === "walletStateAttestation") {
                console.log(
                  `    [${i}] kind: ${r.kind} (walletStateAttestation)  label: ${label}`,
                )
                console.log(`         issuerJwksUri:  ${decoded.issuerJwksUri}`)
                console.log(`         conditionHash:  ${decoded.conditionHash}`)
              } else {
                console.log(`    [${i}] kind: ${r.kind}  label: ${label}`)
                if (r.data !== "0x") {
                  console.log(`         data: ${r.data}`)
                }
              }
            }
          }
        } catch {
          // predicate may not implement getRequirements
        }
      }
    }

    if (options.checkAccess) {
      if (!isAddress(options.checkAccess)) {
        console.error(
          pc.red("Error: --check-access must be a valid Ethereum address"),
        )
        process.exit(1)
      }
      const address: Address = options.checkAccess
      console.log(pc.cyan(`\nAccess check for ${address}:`))
      try {
        const { ok, granted } = await client.tryHasAccess(toolId, address, "0x")
        console.log(
          `  ok: ${ok} (${ok ? "predicate responded normally" : "predicate misbehaved"})`,
        )
        console.log(`  granted: ${granted}`)
      } catch (err) {
        console.error(
          pc.red(
            `  Error: ${err instanceof Error ? err.message : String(err)}`,
          ),
        )
      }
    }

    console.log(pc.cyan("\nFetching manifest from metadata URI..."))

    // metadataURI comes from the (permissionlessly writable) onchain registry.
    // Validate it before fetching so a hostile entry cannot point this fetch at
    // an internal address (e.g. cloud metadata at 169.254.169.254) from the
    // machine or CI runner that runs `inspect`.
    let metadataUrl: URL
    try {
      metadataUrl = new URL(config.metadataURI)
    } catch {
      console.error(
        pc.red(`Error: invalid metadata URI: ${config.metadataURI}`),
      )
      process.exit(1)
    }
    if (metadataUrl.protocol !== "http:" && metadataUrl.protocol !== "https:") {
      console.error(
        pc.red(
          `Error: metadata URI must use http(s), got "${metadataUrl.protocol}"`,
        ),
      )
      process.exit(1)
    }
    if (isPrivateHostname(metadataUrl.hostname)) {
      console.error(
        pc.red(
          `Error: metadata URI host "${metadataUrl.hostname}" is a private/internal address; refusing to fetch`,
        ),
      )
      process.exit(1)
    }
    // The lexical check above only inspects the hostname as written. A public
    // name whose A record points inward (localtest.me -> 127.0.0.1) passes it,
    // and this fetch connects to whatever the resolver returns. Registry
    // entries are permissionlessly writable and can be repointed after review,
    // so resolve and re-check before connecting.
    const resolved = await classifyResolvedHostname(metadataUrl.hostname)
    if (resolved === "private") {
      console.error(
        pc.red(
          `Error: metadata URI host "${metadataUrl.hostname}" resolves to a private/internal address; refusing to fetch`,
        ),
      )
      process.exit(1)
    }
    // Unlike probe-endpoint, this hostname is untrusted: it comes from a
    // permissionlessly writable registry. fetch resolves independently of the
    // lookup above, so an authoritative server that answers SERVFAIL here and
    // hands undici a private address there would defeat the check. Refuse
    // rather than fetch a host we could not classify.
    if (resolved === "unresolved") {
      console.error(
        pc.red(
          `Error: metadata URI host "${metadataUrl.hostname}" could not be resolved; refusing to fetch`,
        ),
      )
      process.exit(1)
    }

    let response: globalThis.Response
    try {
      response = await fetch(config.metadataURI, {
        redirect: "manual",
        signal: AbortSignal.timeout(10_000),
      })
    } catch {
      console.error(pc.red(`Error: Failed to fetch ${config.metadataURI}`))
      process.exit(1)
    }

    if (response.status !== 200) {
      console.error(
        pc.red(`FAIL: Metadata URI returned HTTP ${response.status}`),
      )
      process.exit(1)
    }

    let data: unknown
    try {
      data = await response.json()
    } catch {
      console.error(pc.red("FAIL: Response is not valid JSON"))
      process.exit(1)
    }

    const result = validateManifest(data)
    if (!result.success) {
      console.error(pc.red("\nManifest validation: FAIL"))
      for (const issue of result.error.issues) {
        console.error(pc.yellow(`  ${issue.path.join(".")}: ${issue.message}`))
      }
      process.exit(1)
    }

    const manifest = result.data
    console.log(pc.green("\nManifest validation: PASS"))
    console.log(`  Name: ${manifest.name}`)
    console.log(`  Endpoint: ${manifest.endpoint}`)

    if (
      manifest.pricing &&
      manifest.pricing.length > 0 &&
      config.accessPredicate !== zeroAddress
    ) {
      console.log(
        pc.yellow(
          "\n  ⚠ This tool uses both a payment gate and an access predicate. Clients must satisfy both — SIWE authentication for the predicate AND x402 payment.",
        ),
      )
    }

    // Cross-check against the onchain hash, which commits the manifest as
    // served (ERC-8257 §2). Hash the full fetched document, not the stripped
    // copy.
    const computedHash = computeManifestHash(data as object)
    console.log(pc.cyan("\nHash cross-check:"))
    console.log(`  Onchain:   ${config.manifestHash}`)
    console.log(`  Computed:  ${computedHash}`)

    if (computedHash === config.manifestHash) {
      console.log(pc.green("  Result:    MATCH"))
    } else {
      console.error(pc.red("  Result:    MISMATCH"))
      process.exit(1)
    }

    const probeResult = await probeEndpoint(manifest.endpoint)
    printProbeResult(probeResult)
  })
